import React, {useEffect, useState} from 'react'
import {useAuth} from '../../modules/auth'
import axios from 'axios'
import styles from './DocumentView.module.css'
import {FaFilePdf} from 'react-icons/fa'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import moment from 'moment'
import {Spinner} from 'react-bootstrap'
import {ToastContainer, toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

interface DocumentViewProps {
  claimDetails: any
}

const DocumentView: React.FC<DocumentViewProps> = ({claimDetails}) => {
  const user = useAuth()
  const {auth} = useAuth()
  const claimData = claimDetails?.claimDetails?.claimData
    ? JSON.parse(claimDetails.claimDetails.claimData)
    : null
  const detailBenefit = claimDetails?.claimDetails?.detailBenefit
    ? JSON.parse(claimDetails.claimDetails.detailBenefit)
    : null
  const [icdNames, setIcdNames] = useState<Record<string, string>>({})
  const [memberDetail, setMemberDetail] = useState<any>(null)
  const [claim, setClaim] = useState<any>(null)
  const [fglDocumentUrl, setFglDocumentUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const temp = JSON.parse(claimDetails.claimDetails.memberDetail)

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  useEffect(() => {
    if (temp) {
      setMemberDetail(temp)
      setClaim(claimDetails.claimDetails.claimNumber)
    } else {
      console.warn('Claim details not found')
    }
  }, [claimDetails])

  // Fetch ICD names
  useEffect(() => {
    const fetchIcdNames = async () => {
      // Check if we already have these ICD names to prevent unnecessary fetches
      const icdCodes = [
        claimData?.IcdCode1,
        claimData?.IcdCode2,
        claimData?.IcdCode3,
        claimData?.IcdCode4,
      ].filter(Boolean)

      // Skip if no ICD codes or if we already have all the names
      const missingIcds = icdCodes.filter((code) => !icdNames[code])
      if (missingIcds.length === 0) return

      const names: Record<string, string> = {...icdNames} // Preserve existing names
      for (const code of missingIcds) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_CLAIM_URL}/api/MstIcd10/GetPaginatedMstIcd10s?searchTerm=${code}&pageNumber=1&pageSize=10`,
            {
              headers: {
                Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
              },
            }
          )
          const matchingIcd = response.data.data.find((icd: any) => icd.code === code)
          if (matchingIcd) {
            names[code] = matchingIcd.name
          }
        } catch (error) {
          console.error(`Error fetching ICD name for ${code}:`, error)
        }
      }
      setIcdNames(names)
    }

    if (claimData) {
      fetchIcdNames()
    }
  }, [claimData, user.currentUser?.token]) // Remove icdNames from dependency array

  // Calculate totals
  const calculateTotals = () => {
    if (!claimData?.MapClaimPaid) return {totalClaim: 0, totalCover: 0, totalExcess: 0}

    return claimData.MapClaimPaid.reduce(
      (acc: any, item: any) => ({
        totalClaim: acc.totalClaim + (Number(item.ClaimAmountTo) || 0),
        totalCover: acc.totalCover + (Number(item.CoverAmount) || 0),
        totalExcess: acc.totalExcess + (Number(item.ExcessAmount) || 0),
      }),
      {totalClaim: 0, totalCover: 0, totalExcess: 0}
    )
  }

  // Filter unique benefits
  const getUniqueBenefits = () => {
    if (!claimData?.MapClaimPaid) return []

    const uniqueBenefits = new Map()
    claimData.MapClaimPaid.forEach((item: any) => {
      const benefit = claimData.TransBenefit?.find((b: any) => b.Id === item.TransBenefitId)
      if (
        benefit &&
        !uniqueBenefits.has(benefit.SubBenefitName) &&
        Number(item.ClaimAmountTo) > 0
      ) {
        uniqueBenefits.set(benefit.SubBenefitName, item)
      }
    })
    return Array.from(uniqueBenefits.values())
  }

  const handleSavePDF = async () => {
    const content = document.getElementById('document-content')
    if (!content) return

    try {
      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        logging: false,
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`claim-${claimDetails?.claimDetails?.claimNumber || 'document'}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
    }
  }

  // Fetch FGL Document URL on component mount or when claimDetails change
  useEffect(() => {
    const fetchFglUrl = async () => {
      if (!claimDetails?.claimDetails?.claimNumber) {
        console.warn('Claim number not available for fetching FGL.')
        return
      }
      setIsLoading(true) // Start loading
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Attachment/DownloadFGL/${claimDetails.claimDetails.claimNumber}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch FGL document URL')
        }

        const data = await response.json()

        if (data && data.url) {
          setFglDocumentUrl(data.url) // Store the URL in state
          setIsLoading(false) // Stop loading on success
          toast.success('FGL document loaded!', {
            position: 'top-right',
            autoClose: 3000,
          })
        } else {
          throw new Error('Invalid response format for FGL URL')
        }
      } catch (error) {
        console.error('Error fetching FGL URL:', error)
        toast.error('Failed to load FGL document', {
          position: 'top-right',
          autoClose: 3000,
        })
        setFglDocumentUrl(null) // Reset URL on error
        setIsLoading(false) // Stop loading on error
      }
    }

    fetchFglUrl() // Fetch the URL when the component mounts or claimDetails change
  }, [claimDetails, auth?.token, user.currentUser?.token]) // Dependencies for fetching

  // Optional: Keep the download function if you still want a separate download button
  const handleDownloadFGL = () => {
    if (fglDocumentUrl) {
      window.open(fglDocumentUrl, '_blank')
      toast.success('FGL download started!', {
        position: 'top-right',
        autoClose: 3000,
      })
    } else {
      toast.error('FGL document URL not available for download.', {
        position: 'top-right',
        autoClose: 3000,
      })
    }
  }

  return (
    <>
      {/* Optional: Keep or remove the download button based on your needs */}
      <button
        onClick={handleDownloadFGL}
        disabled={!fglDocumentUrl} // Disable if no URL or loading
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: !fglDocumentUrl || isLoading ? '#ccc' : '#059BB6', // Grey out when disabled
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: !fglDocumentUrl || isLoading ? 'not-allowed' : 'pointer', // Change cursor when disabled
          marginBottom: '16px',
        }}
      >
        <FaFilePdf /> Download FGL
      </button>

      <div
        id='document-content'
        className={styles.container}
        style={{height: '80vh' /* Adjust height as needed */}}
      >
        {isLoading && (
          <div
            className={styles.loading} // Keep existing class if it has other styles
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%', // Ensure it fills the container height
            }}
          >
            <Spinner animation='border' role='status' style={{color: '#059BB6'}}>
              <span className='visually-hidden'>Loading...</span>
            </Spinner>
            <span style={{marginLeft: '10px', color: '#059BB6', fontWeight: 500}}>
              Loading FGL document...
            </span>
          </div>
        )}
        {!isLoading && fglDocumentUrl && (
          <iframe
            // Use Google Docs viewer for broader compatibility (PDF, DOCX, etc.)
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(
              fglDocumentUrl
            )}&embedded=true`}
            style={{
              width: '100%',
              height: '100%', // Make iframe fill the container
              border: 'none', // Removes browser border
            }}
            title='FGL Document Preview'
            frameBorder='0' // Also removes browser border (legacy attribute)
            onLoad={() => setIsLoading(false)} // Set loading to false only when iframe content is loaded
          />
        )}
        {fglDocumentUrl && isLoading && (
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(
              fglDocumentUrl
            )}&embedded=true`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'none', // Hide the iframe while loading
            }}
            title='FGL Document Preloader'
            frameBorder='0'
            onLoad={() => {
              // Give it a moment to render properly
              setTimeout(() => {
                setIsLoading(false)
              }, 1000)
            }}
          />
        )}
        {!isLoading && !fglDocumentUrl && (
          <div
            className={styles.error} // Keep existing class
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            Failed to load FGL document preview.
          </div>
        )}
      </div>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        style={{zIndex: 9999}}
      />
    </>
  )
}

export default DocumentView
